import React, { FC } from "react";
import { ModalDialogProvider } from "../../../components";
import FocusBox from "../../../components/components/molecules/FocusBox";
import { AllIcons, Flex, Heading, Text, Container, Link, List, Icon } from "@telia/teddy";
import _map from "lodash/map";
import { connect } from "react-redux";
import { RootErrorState } from "../../../../../models";
import { ErrorState } from "../../../../../models/state";
import { ConnectedPageHeader, ConnectedPageFooter } from "../../../navigation";
import InvalidOrderModal from "../molecules/InvalidOrderModal/InvalidOrderModal";
import { useBreakpoint, useQueryParam } from "../../../components/hooks";

interface ErrorDataProps {
    description: string;
    errorData: ErrorState;
    title: string;
    isMobile: boolean;
}

const headerProps = { justify: "center", align: "center", direction: "column", p: { sm: "16px", lg: "40px" } };

const Error404: FC<ErrorDataProps> = ({ errorData, title, description, isMobile }) => {
    if (errorData?.invalidOrder) {
        return <InvalidOrderModal />;
    } else if (errorData?.ancestorUrls) {
        return <Product404Error {...errorData} />;
    } else return <Generic404Error isMobile={isMobile} title={title} description={description} />;
};

const Product404Error: FC<Pick<ErrorState, "url" | "ancestorUrls">> = ({ url, ancestorUrls }) => (
    <div className="page-wrapper">
        <Flex {...headerProps}>
            <Heading as="h2" variant="title-500">
                Oisann!
            </Heading>
            <Text as="p" variant="preamble-200">
                404 - Produktet finnes ikke lenger
            </Text>
        </Flex>
        <Container mt="400">
            <FocusBox>
                <Text mb="100">
                    Beklager, produktet med adresse <span className="telia-sans-medium">{decodeURIComponent(url)}</span>{" "}
                    selges dessverre ikke lenger.
                </Text>
                <Text mb="100">
                    Prøv gjerne å finne et lignende produkt fra utvalget vårt, for eksempel en av disse:
                </Text>
                <List layout="vertical" gapSize="small">
                    {_map(ancestorUrls, (ancestorUrl, i) => (
                        <List.Item key={i}>
                            <List.Illustration>
                                <Icon name="chevron-right" size="sm" aria-hidden="true" />
                            </List.Illustration>
                            <List.Content>
                                <Link variant="text" href={"/" + ancestorUrl}>
                                    {ancestorUrl}
                                </Link>
                            </List.Content>
                        </List.Item>
                    ))}
                </List>
            </FocusBox>
        </Container>
    </div>
);

const Generic404Error: FC<Pick<ErrorDataProps, "title" | "description" | "isMobile">> = ({
    title,
    description,
    isMobile,
}) => (
    <div className="page-wrapper">
        <Flex {...headerProps}>
            <Heading as="h1" variant={isMobile ? "display-50" : "display-100"}>
                Oisann!
            </Heading>
            <Text variant="preamble-200">{title || "Her var det visst ingenting å hente annet enn 404."}</Text>
        </Flex>
        <Container mt="400">
            <FocusBox>
                {!description && (
                    <>
                        <Heading mb="100" as="h2" variant="title-200">
                            Siden finnes ikke
                        </Heading>
                        <Text>
                            Vi beklager, men vi fant ikke siden du så etter. Du kan jo gå til{" "}
                            <Link variant="text" href="/">
                                forsiden
                            </Link>
                            , eller prøve å finne noe annet via menyen.
                        </Text>
                    </>
                )}
                {description}
            </FocusBox>
        </Container>
    </div>
);

const GenericError: FC<Pick<ErrorState, "errorHeader" | "errorMessage">> = ({ errorHeader, errorMessage }) => (
    <div className="page-wrapper">
        <Flex {...headerProps}>
            <Heading as="h2" variant="title-500">
                Oisann!
            </Heading>
            <Text as="p" variant="preamble-200" mt="150">
                {errorHeader || "Server error"}
            </Text>
        </Flex>
        <Container mt="400">
            <FocusBox>
                <div className="paragraph" dangerouslySetInnerHTML={{ __html: errorMessage }}></div>
            </FocusBox>
        </Container>
    </div>
);

const ErrorPageRoot: FC<Pick<ErrorDataProps, "errorData">> = ({ errorData }) => {
    const title = useQueryParam("title") ?? "";
    const description = useQueryParam("description") ?? "";
    const isMobile = !useBreakpoint("xs");

    return (
        <ModalDialogProvider>
            <AllIcons />
            <ConnectedPageHeader />
            {errorData?.errorCode === 404 ? (
                <Error404 errorData={errorData} title={title} description={description} isMobile={isMobile} />
            ) : (
                <GenericError {...errorData} />
            )}
            <ConnectedPageFooter />
        </ModalDialogProvider>
    );
};

const mapStateToProps = (state: RootErrorState) => ({
    errorData: state.error,
});

export default connect(mapStateToProps)(ErrorPageRoot);
